// Motion for an animated-logo/v1 spec's moving part (the group marked
// data-motion-part, drawn centred on its own origin). Pure functions plus a
// small Web Animations player; no Stimulus dependency.
//
// A play turns the part from -turnDegrees back to 0 (one arm-step for a
// symmetric mark, so start and end look identical) and, for pulsing presets,
// swells the stroke and scale on a half sine. Frame 0 and the end of every
// play are both the static rest pose.

export const EASINGS = {
  linear: () => t => t,
  "out-cubic": () => t => 1 - Math.pow(1 - t, 3),
  "out-back": overshoot => t => 1 + (overshoot + 1) * Math.pow(t - 1, 3) + overshoot * Math.pow(t - 1, 2),
}

const clamp01 = value => Math.min(1, Math.max(0, value))

export function playFrames(motion) {
  return Math.max(motion.frames, motion.pulse ? motion.pulse.frames : 0)
}

export function poseAt(motion, frame) {
  const ease = (EASINGS[motion.ease] || EASINGS["out-cubic"])(motion.overshoot ?? 1.70158)
  const rotate = motion.turnDegrees ? -motion.turnDegrees * (1 - ease(clamp01(frame / motion.frames))) : 0
  const swell = motion.pulse ? Math.sin(Math.PI * clamp01(frame / motion.pulse.frames)) : 0
  const scale = motion.pulse ? 1 + ((motion.pulse.scale ?? 1) - 1) * swell : 1
  return { rotate, scale, swell }
}

export function buildTimeline(motion, samplesPerFrame = 2) {
  const fps = motion.fps || 30
  const frames = playFrames(motion)
  const playMs = (frames / fps) * 1000
  const loop = typeof motion.everyS === "number"
  const durationMs = loop ? Math.max(playMs, motion.everyS * 1000) : playMs
  const steps = Math.max(1, Math.ceil(frames * samplesPerFrame))
  const poses = []
  for (let i = 0; i <= steps; i++) {
    const frame = (frames * i) / steps
    poses.push({ offset: clamp01(((frame / fps) * 1000) / durationMs), ...poseAt(motion, frame) })
  }
  if (poses[poses.length - 1].offset < 1) poses.push({ offset: 1, rotate: 0, scale: 1, swell: 0 })
  return {
    durationMs,
    loop,
    offsetMs: loop ? ((motion.offsetS || 0) * 1000) % durationMs : 0,
    transform: poses.map(p => ({ offset: p.offset, transform: `rotate(${p.rotate.toFixed(3)}deg) scale(${p.scale.toFixed(4)})` })),
    swell: motion.pulse ? poses.map(p => ({ offset: p.offset, swell: p.swell })) : null,
  }
}

// Each shape swells from its own width by the preset's peak/base ratio, so a
// heavier small cut keeps its proportions.
export function strokeKeyframes(timeline, motion, baseWidth) {
  const peak = baseWidth * (motion.pulse.peakStroke / (motion.baseStroke || baseWidth))
  return timeline.swell.map(({ offset, swell }) => ({
    offset,
    strokeWidth: `${(baseWidth + (peak - baseWidth) * swell).toFixed(3)}px`,
  }))
}

export function reducedMotionQuery() {
  return typeof window.matchMedia === "function" ? window.matchMedia("(prefers-reduced-motion: reduce)") : null
}

export class LogoMotionPlayer {
  constructor(part, motion) {
    const timeline = buildTimeline(motion)
    const timing = { duration: timeline.durationMs, iterations: timeline.loop ? Infinity : 1, easing: "linear" }
    this.loop = timeline.loop
    this.done = false
    this.animations = [part.animate(timeline.transform, timing)]
    if (timeline.swell) {
      part.querySelectorAll("path, line, circle, rect").forEach(shape => {
        const base = parseFloat(shape.getAttribute("stroke-width"))
        if (base > 0) this.animations.push(shape.animate(strokeKeyframes(timeline, motion, base), timing))
      })
    }
    this.animations.forEach(animation => {
      animation.pause()
      animation.currentTime = timeline.offsetMs
    })
    if (!this.loop) this.animations[0].finished?.then(() => { this.done = true }).catch(() => {})
  }

  play() {
    if (!this.done) this.animations.forEach(animation => animation.play())
  }

  pause() {
    this.animations.forEach(animation => animation.pause())
  }

  cancel() {
    this.done = true
    this.animations.forEach(animation => animation.cancel())
  }
}
